'use client';

import React, { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface Issue {
  id: string;
  title: string;
  description: string;
  severity: 'low' | 'medium' | 'high' | 'critical';
  estimatedTime: string;
  basePrice: number;
  icon: string;
  image: string;
  alt: string;
  category: 'display' | 'battery' | 'water' | 'software' | 'hardware';
}

interface IssueSelectorProps {
  onIssueSelect: (issues: Issue[]) => void;
  selectedIssues: Issue[];
  deviceType: string;
}

const IssueSelector = ({ onIssueSelect, selectedIssues, deviceType }: IssueSelectorProps) => {
  const [activeCategory, setActiveCategory] = useState<'display' | 'battery' | 'water' | 'software' | 'hardware'>('display');

  const issues: Issue[] = [
  // Display Issues
  {
    id: 'cracked-screen',
    title: 'Cracked Screen',
    description: 'Visible cracks or shattered glass on display',
    severity: 'high',
    estimatedTime: '45-60 min',
    basePrice: 149,
    icon: 'DevicePhoneMobileIcon',
    category: 'display',
    image: "https://images.unsplash.com/photo-1636642823100-2580cdeb15fc",
    alt: 'Smartphone with severely cracked screen showing spider web pattern damage'
  },
  {
    id: 'black-screen',
    title: 'Black Screen',
    description: 'Display not showing anything, completely black',
    severity: 'critical',
    estimatedTime: '30-45 min',
    basePrice: 179,
    icon: 'ComputerDesktopIcon',
    category: 'display',
    image: "https://images.unsplash.com/photo-1712320861715-25f320ad66fa",
    alt: 'Black smartphone screen reflecting light showing no display output'
  },
  {
    id: 'touch-not-working',
    title: 'Touch Not Working',
    description: 'Screen displays but touch input not responding',
    severity: 'high',
    estimatedTime: '60-90 min',
    basePrice: 129,
    icon: 'HandRaisedIcon',
    category: 'display',
    image: "https://images.unsplash.com/photo-1597165508161-a2fac88c695e",
    alt: 'Finger touching smartphone screen with visible touch points highlighted'
  },

  // Battery Issues
  {
    id: 'battery-drain',
    title: 'Fast Battery Drain',
    description: 'Battery depletes quickly, poor battery life',
    severity: 'medium',
    estimatedTime: '30-45 min',
    basePrice: 89,
    icon: 'BatteryIcon',
    category: 'battery',
    image: "https://images.unsplash.com/photo-1679285516851-4f0cb04a3893",
    alt: 'Smartphone showing low battery warning icon on red background'
  },
  {
    id: 'not-charging',
    title: 'Not Charging',
    description: 'Device does not charge when plugged in',
    severity: 'high',
    estimatedTime: '45-60 min',
    basePrice: 79,
    icon: 'BoltIcon',
    category: 'battery',
    image: "https://images.unsplash.com/photo-1701695356849-8f6354a44309",
    alt: 'USB charging cable connected to smartphone port close-up view'
  },
  {
    id: 'swollen-battery',
    title: 'Swollen Battery',
    description: 'Battery expansion causing device deformation',
    severity: 'critical',
    estimatedTime: '60-90 min',
    basePrice: 119,
    icon: 'ExclamationTriangleIcon',
    category: 'battery',
    image: "https://images.unsplash.com/photo-1639776738823-be3dc91bb5d2",
    alt: 'Smartphone with back cover slightly separated due to swollen battery'
  },

  // Water Damage
  {
    id: 'water-damage',
    title: 'Water Damage',
    description: 'Device exposed to liquid, potential internal damage',
    severity: 'critical',
    estimatedTime: '2-4 hours',
    basePrice: 199,
    icon: 'BeakerIcon',
    category: 'water',
    image: "https://images.unsplash.com/photo-1449136250873-cea416e4d13d",
    alt: 'Smartphone submerged in clear water with visible water droplets'
  },
  {
    id: 'moisture-detected',
    title: 'Moisture in Ports',
    description: 'Charging port shows moisture detection warning',
    severity: 'medium',
    estimatedTime: '1-2 hours',
    basePrice: 69,
    icon: 'CloudIcon',
    category: 'water',
    image: "https://images.unsplash.com/photo-1695482482959-937db8e81cef",
    alt: 'Close-up of smartphone charging port with moisture warning indicator'
  },

  // Software Issues
  {
    id: 'wont-turn-on',
    title: 'Won\'t Turn On',
    description: 'Device completely unresponsive, no power',
    severity: 'critical',
    estimatedTime: '30-60 min',
    basePrice: 59,
    icon: 'PowerIcon',
    category: 'software',
    image: "https://images.unsplash.com/uploads/141225981516058b8b118/38d3c453",
    alt: 'Smartphone with blank black screen and power button highlighted'
  },
  {
    id: 'frozen-device',
    title: 'Frozen/Slow Performance',
    description: 'Device freezes frequently or runs very slowly',
    severity: 'medium',
    estimatedTime: '45-90 min',
    basePrice: 79,
    icon: 'ClockIcon',
    category: 'software',
    image: "https://images.unsplash.com/photo-1644579506607-1cec7c3d152c",
    alt: 'Smartphone screen showing loading spinner and frozen interface'
  },

  // Hardware Issues
  {
    id: 'camera-not-working',
    title: 'Camera Issues',
    description: 'Camera app crashes or produces poor quality images',
    severity: 'medium',
    estimatedTime: '60-90 min',
    basePrice: 109,
    icon: 'CameraIcon',
    category: 'hardware',
    image: "https://images.unsplash.com/photo-1609234173662-e3bb006ca845",
    alt: 'Smartphone camera lens with visible crack and blurred focus'
  },
  {
    id: 'speaker-issues',
    title: 'Speaker Problems',
    description: 'No sound, distorted audio, or low volume',
    severity: 'medium',
    estimatedTime: '45-75 min',
    basePrice: 89,
    icon: 'SpeakerWaveIcon',
    category: 'hardware',
    image: "https://images.unsplash.com/photo-1583573604342-10cf930b914a",
    alt: 'Smartphone speaker grille with sound wave visualization overlay'
  }];


  const categories = [
  { id: 'display', name: 'Display', icon: 'ComputerDesktopIcon', color: 'text-blue-600' },
  { id: 'battery', name: 'Battery', icon: 'BatteryIcon', color: 'text-green-600' },
  { id: 'water', name: 'Water Damage', icon: 'BeakerIcon', color: 'text-cyan-600' },
  { id: 'software', name: 'Software', icon: 'CodeBracketIcon', color: 'text-purple-600' },
  { id: 'hardware', name: 'Hardware', icon: 'CpuChipIcon', color: 'text-orange-600' }];


  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'low':return 'text-green-600 bg-green-50';
      case 'medium':return 'text-yellow-600 bg-yellow-50';
      case 'high':return 'text-orange-600 bg-orange-50';
      case 'critical':return 'text-red-600 bg-red-50';
      default:return 'text-gray-600 bg-gray-50';
    }
  };

  const filteredIssues = issues.filter((issue) => issue.category === activeCategory);

  const handleIssueToggle = (issue: Issue) => {
    const isSelected = selectedIssues.some((selected) => selected.id === issue.id);
    if (isSelected) {
      onIssueSelect(selectedIssues.filter((selected) => selected.id !== issue.id));
    } else {
      onIssueSelect([...selectedIssues, issue]);
    }
  };

  return (
    <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:215:4" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="215" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-6%22%7D" className="space-y-6">
      {/* Category Tabs */}
      <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:217:6" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="217" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
        {categories.map((category) =>
        <button data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:219:8" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="219" data-component-file="IssueSelector.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
          key={category.id}
          onClick={() => setActiveCategory(category.id as any)}
          className={`flex items-center space-x-2 px-4 py-2 rounded-lg font-medium transition-all duration-300 ${
          activeCategory === category.id ?
          'bg-primary text-primary-foreground shadow-premium' :
          'bg-muted text-muted-foreground hover:bg-muted/80'}`
          }>

            <Icon data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:228:12" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="228" data-component-file="IssueSelector.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%7D" name={category.icon as any} size={18} className={activeCategory === category.id ? '' : category.color} />
            <span data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:229:12" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="229" data-component-file="IssueSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{category.name}</span>
          </button>
        )}
      </div>

      {/* Issues Grid */}
      <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:235:6" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="235" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-4%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {filteredIssues.map((issue) => {
          const isSelected = selectedIssues.some((selected) => selected.id === issue.id);
          return (
            <button data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:239:12" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="239" data-component-file="IssueSelector.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%7D"
              key={issue.id}
              onClick={() => handleIssueToggle(issue)}
              className={`card p-4 text-left hover:shadow-elevation transition-all duration-300 hover-scale ${
              isSelected ? 'ring-2 ring-primary bg-primary/5' : ''}`
              }>

              <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:246:14" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="246" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20space-x-4%22%7D" className="flex space-x-4">
                <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:247:16" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="247" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22w-20%20h-20%20bg-muted%20rounded-lg%20overflow-hidden%20flex-shrink-0%22%7D" className="w-20 h-20 bg-muted rounded-lg overflow-hidden flex-shrink-0">
                  <AppImage data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:248:18" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="248" data-component-file="IssueSelector.tsx" data-component-name="AppImage" data-component-content="%7B%22elementName%22%3A%22AppImage%22%2C%22className%22%3A%22w-full%20h-full%20object-cover%22%7D"
                    src={issue.image}
                    alt={issue.alt}
                    className="w-full h-full object-cover" />

                </div>
                <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:254:16" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="254" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex-1%20min-w-0%22%7D" className="flex-1 min-w-0">
                  <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:255:18" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="255" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-start%20justify-between%20mb-2%22%7D" className="flex items-start justify-between mb-2">
                    <h3 data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:256:20" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="256" data-component-file="IssueSelector.tsx" data-component-name="h3" data-component-content="%7B%22elementName%22%3A%22h3%22%2C%22className%22%3A%22font-semibold%20text-card-foreground%22%7D" className="font-semibold text-card-foreground">{issue.title}</h3>
                    {isSelected &&
                    <Icon data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:258:20" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="258" data-component-file="IssueSelector.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22CheckCircleIcon%22%2C%22className%22%3A%22text-primary%20flex-shrink-0%22%7D" name="CheckCircleIcon" size={20} className="text-primary flex-shrink-0" />
                    }
                  </div>
                  <p data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:261:18" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="261" data-component-file="IssueSelector.tsx" data-component-name="p" data-component-content="%7B%22elementName%22%3A%22p%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%20mb-3%20line-clamp-2%22%7D" className="text-sm text-muted-foreground mb-3 line-clamp-2">{issue.description}</p>
                  <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:262:18" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="262" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20justify-between%22%7D" className="flex items-center justify-between">
                    <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:263:20" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="263" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-2%22%7D" className="flex items-center space-x-2">
                      <span data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:264:22" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="264" data-component-file="IssueSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D" className={`px-2 py-1 rounded-full text-xs font-medium ${getSeverityColor(issue.severity)}`}>
                        {issue.severity.charAt(0).toUpperCase() + issue.severity.slice(1)}
                      </span>
                      <span data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:267:22" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="267" data-component-file="IssueSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-xs%20text-muted-foreground%22%7D" className="text-xs text-muted-foreground">{issue.estimatedTime}</span>
                    </div>
                    <span data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:269:20" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="269" data-component-file="IssueSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-semibold%20text-primary%22%2C%22textContent%22%3A%22%24%22%7D" className="font-semibold text-primary">${issue.basePrice}</span>
                  </div>
                </div>
              </div>
            </button>);

        })}
      </div>

      {selectedIssues.length > 0 &&
      <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:279:6" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="279" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22bg-primary%2F5%20border%20border-primary%2F20%20rounded-lg%20p-4%22%7D" className="bg-primary/5 border border-primary/20 rounded-lg p-4">
          <h4 data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:280:10" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="280" data-component-file="IssueSelector.tsx" data-component-name="h4" data-component-content="%7B%22elementName%22%3A%22h4%22%2C%22className%22%3A%22font-semibold%20text-primary%20mb-2%22%2C%22textContent%22%3A%22Selected%20Issues%20(%20)%22%7D" className="font-semibold text-primary mb-2">Selected Issues ({selectedIssues.length})</h4>
          <div data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:281:10" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="281" data-component-file="IssueSelector.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20flex-wrap%20gap-2%22%7D" className="flex flex-wrap gap-2">
            {selectedIssues.map((issue) =>
          <span data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:283:10" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="283" data-component-file="IssueSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22inline-flex%20items-center%20space-x-1%20bg-primary%20text-primary-foreground%20px-3%20py-1%20rounded-full%20text-sm%22%7D"
            key={issue.id}
            className="inline-flex items-center space-x-1 bg-primary text-primary-foreground px-3 py-1 rounded-full text-sm">

                <span data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:287:16" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="287" data-component-file="IssueSelector.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D">{issue.title}</span>
                <button data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:288:16" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="288" data-component-file="IssueSelector.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22hover%3Abg-primary-foreground%2F20%20rounded-full%20p-0.5%22%7D"
              onClick={(e) => {
                e.stopPropagation();
                handleIssueToggle(issue);
              }}
              className="hover:bg-primary-foreground/20 rounded-full p-0.5">

                  <Icon data-component-id="src/app/instant-quote-tool/components/IssueSelector.tsx:295:18" data-component-path="src/app/instant-quote-tool/components/IssueSelector.tsx" data-component-line="295" data-component-file="IssueSelector.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={14} />
                </button>
              </span>
          )}
          </div>
        </div>
      }
    </div>);

};

export default IssueSelector;